/**
 * Review system service layer.
 * All functions use apiClient to communicate with the NestJS backend.
 */
import { apiClient } from '@/lib/api-client';
import type {
  FeedbackStatus,
  ReviewPage,
  FeedbackSubmission,
  AnsweredFilter,
  ContactChannel,
  FeedbackStatusCounts,
} from '@/types/reviews';

// ── Review Pages ───────────────────────────────────────────────

export async function fetchReviewPages(): Promise<ReviewPage[]> {
  return apiClient.get<ReviewPage[]>('/reviews/pages');
}

/** MVP single-page model: returns the org's only review page (auto-creating a default). */
export async function fetchMyReviewPage(): Promise<ReviewPage> {
  return apiClient.get<ReviewPage>('/reviews/pages/me');
}

export async function fetchReviewPageById(id: string): Promise<ReviewPage> {
  return apiClient.get<ReviewPage>('/reviews/pages/' + id);
}

export async function insertReviewPage(
  page: Pick<ReviewPage, 'organizationId' | 'slug'> &
    Partial<Pick<ReviewPage, 'redirectUrlPositive' | 'brandingJson'>>,
): Promise<ReviewPage> {
  return apiClient.post<ReviewPage>('/reviews/pages', page);
}

export async function updateReviewPage(
  id: string,
  updates: Partial<Omit<ReviewPage, 'id' | 'organizationId' | 'createdAt' | 'updatedAt'>>,
): Promise<ReviewPage> {
  return apiClient.patch<ReviewPage>('/reviews/pages/' + id, updates);
}

export async function deleteReviewPage(id: string): Promise<void> {
  await apiClient.delete('/reviews/pages/' + id);
}

// ── Duplicate ──────────────────────────────────────────────────

export async function duplicateReviewPage(id: string): Promise<ReviewPage> {
  return apiClient.post<ReviewPage>('/reviews/pages/' + id + '/duplicate');
}

// ── Logo upload ────────────────────────────────────────────────

export async function uploadReviewLogo(
  orgId: string,
  file: File,
): Promise<string> {
  // Use FormData for file upload — must bypass the JSON apiClient
  const { auth } = await import('@/services/auth');
  const { data: sessionData } = await auth.getSession();
  const token = sessionData?.session?.accessToken;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('orgId', orgId);

  // See the note in lib/api-client.ts.
  const API_URL = import.meta.env.VITE_API_URL || '/api';
  const response = await fetch(`${API_URL}/reviews/pages/upload-logo`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text);
  }

  const data = await response.json();
  return data.url;
}

// ── Feedback Submissions ───────────────────────────────────────

export interface FeedbackFilterParams {
  status?: FeedbackStatus;
  assignedTo?: 'me' | 'unassigned' | string;
  score?: 'positive' | 'negative';
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  /** Whether the customer has heard back — answered / waiting / unreachable. */
  answered?: AnsweredFilter;
}

export interface PaginatedFeedback {
  data: FeedbackSubmission[];
  total: number;
  page: number;
  pageSize: number;
  /**
   * One count per status, computed WITHOUT the status filter so the facet's
   * chips describe what each one would open.
   *
   * Typed as a full `Record<FeedbackStatus, number>` on purpose. It used to name
   * four of the five statuses, which meant every positive submission (`logged`)
   * was counted server-side and then dropped, and any consumer summing the
   * object got a number smaller than the list it sat above.
   */
  counts: FeedbackStatusCounts;
}

export interface FeedbackAnalyticsResponse {
  totals: {
    total: number;
    positive: number;
    negative: number;
    withFeedback: number;
    positiveRate: number;
  };
  daily: Array<{ date: string; positive: number; negative: number }>;
  byLocation: Array<{
    locationId: string | null;
    locationName: string;
    total: number;
    positive: number;
    negative: number;
    positiveRate: number;
  }>;
  window: { dateFrom: string | null; dateTo: string | null };
}

export async function fetchFeedbackAnalytics(
  params: { dateFrom?: string; dateTo?: string } = {},
): Promise<FeedbackAnalyticsResponse> {
  const p: Record<string, string> = {};
  if (params.dateFrom) p.dateFrom = params.dateFrom;
  if (params.dateTo) p.dateTo = params.dateTo;
  return apiClient.get<FeedbackAnalyticsResponse>('/reviews/feedback/analytics', p);
}

/**
 * All-time submission count only. Backs the "view all time" nudge — calling the
 * full analytics aggregation just to read totals.total made every analytics page
 * load scan the org's entire feedback history.
 */
export async function fetchFeedbackCount(): Promise<{ total: number }> {
  return apiClient.get<{ total: number }>('/reviews/feedback/count');
}

export async function fetchFeedbackFiltered(
  params: FeedbackFilterParams = {},
): Promise<PaginatedFeedback> {
  const p: Record<string, string> = {};
  if (params.status) p.status = params.status;
  if (params.score) p.score = params.score;
  if (params.search) p.search = params.search;
  if (params.dateFrom) p.dateFrom = params.dateFrom;
  if (params.dateTo) p.dateTo = params.dateTo;
  if (params.sortBy) p.sortBy = params.sortBy;
  if (params.sortOrder) p.sortOrder = params.sortOrder;
  if (params.page !== undefined) p.page = String(params.page);
  if (params.pageSize !== undefined) p.pageSize = String(params.pageSize);
  if (params.assignedTo) p.assignedTo = params.assignedTo;
  if (params.answered) p.answered = params.answered;
  return apiClient.get<PaginatedFeedback>('/reviews/feedback', p);
}

/**
 * Record contact made outside the app — a call, an email from the team's own
 * inbox, a conversation at the counter. There is no `sms` channel here on
 * purpose: the app sends those itself and records them automatically, so
 * allowing one to be asserted by hand would make the timeline unfalsifiable.
 */
export async function markFeedbackContacted(
  feedbackSubmissionId: string,
  body: { channel: Exclude<ContactChannel, 'sms'>; note?: string },
): Promise<void> {
  await apiClient.post('/reviews/feedback/' + feedbackSubmissionId + '/contacted', body);
}

export async function patchFeedback(
  feedbackSubmissionId: string,
  body: {
    status?: FeedbackStatus;
    resolutionNote?: string;
    internalNotes?: string;
    assignedTo?: string | null;
    /**
     * When transitioning to `resolved`, set this true to ask the backend
     * to SMS the resolutionNote to the customer. The API only sends when
     * a `customerPhone` is on file and the note is non-empty.
     */
    sendToCustomer?: boolean;
  },
): Promise<{ customerNotified: boolean }> {
  // `customerNotified` reports what actually happened, which is not the same as
  // what was asked for: the send is skipped for an opted-out contact, a missing
  // phone or an empty note, and can fail outright. The status change succeeds
  // regardless, so the caller must not infer the SMS from its own request.
  return apiClient.patch<{ customerNotified: boolean }>(
    '/reviews/feedback/' + feedbackSubmissionId,
    body,
  );
}

export async function replyToFeedback(
  feedbackSubmissionId: string,
  body: { channel: 'sms'; message: string },
): Promise<void> {
  await apiClient.post('/reviews/feedback/' + feedbackSubmissionId + '/reply', body);
}

// ── Feedback soft-delete ───────────────────────────────────────

/**
 * Soft-deletes rows. Recoverable one at a time with `restoreFeedback`, which is
 * what the inbox's undo calls.
 *
 * The third `batchId` argument is gone. The server accepted it and never stored
 * it, so it looked like a handle you could keep to bring the whole set back and
 * was not one (A9-06).
 */
export async function softDeleteFeedback(ids: string[], userId: string): Promise<void> {
  await apiClient.post('/reviews/feedback/soft-delete', { ids, userId });
}

// ── Recovery ──────────────────────────────────────────────────

/** Undo for the inbox's bulk delete. Called one id at a time. */
export async function restoreFeedback(feedbackId: string) {
  await apiClient.post('/reviews/feedback/' + feedbackId + '/restore');
}

/*
 * Four functions used to sit around that one, and all four had zero callers:
 *
 *   restoreFeedbackBatch, hardDeleteFeedbackBatch   removed with the two batch
 *     routes, which could never restore anything and could destroy the wrong
 *     rows (A9-06)
 *   fetchDeletedFeedbackMetadata, hardDeleteFeedback   removed here
 *   fetchFeedbackEvents   removed here, and the worst of them: it fetched
 *     GET /reviews/feedback/:id/events, which is not one of the reviews
 *     controller's routes and never has been. Anything that called it got a
 *     404, and nothing did.
 *
 * `restoreFeedback` is the one that survived, because Batch 13 wired the
 * inbox's undo to it. It is a good illustration of why each of these was
 * checked for callers individually rather than deleted as a set: the finding
 * listed all five as dead and one of them had since come alive.
 *
 * The server keeps GET /reviews/feedback/deleted and
 * DELETE /reviews/feedback/:id/hard-delete. See the note in
 * api/src/reviews/reviews.controller.ts for why those two are not going with
 * their wrappers.
 */
